'use client';
import Icon from '@/components/common/Icon';
import { Avatar } from 'antd';
import { signOut } from 'next-auth/react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import styles from './MoreMenuSheet.module.scss';

/**
 * MoreMenuSheet Component
 * Bottom sheet modal for secondary navigation items on mobile
 * Follows iOS/Android "More" menu patterns
 *
 * @param {boolean} isOpen - Whether the sheet is open
 * @param {Function} onClose - Function to close the sheet
 * @param {Object} session - User session object
 */
const MoreMenuSheet = ({ isOpen, onClose, session }) => {
  const sheetRef = useRef(null);

  // Secondary navigation items
  const menuItems = [
    {
      key: 'settings',
      label: 'Settings',
      icon: 'SettingOutlined',
      path: '/account/settings',
    },
    {
      key: 'support',
      label: 'Support',
      icon: 'CustomerServiceOutlined',
      path: '/account/support',
    },
    {
      key: 'divider-1',
      type: 'divider',
    },
    {
      key: 'sign-out',
      label: 'Sign Out',
      icon: 'LogoutOutlined',
      onClick: () => signOut({ callbackUrl: '/auth/signin' }),
    },
  ];

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={onClose} />

      {/* Bottom Sheet */}
      <div ref={sheetRef} className={styles.moreMenuSheet}>
        {/* Drag Handle */}
        <div className={styles.dragHandle}>
          <span className={styles.dragHandleLine} />
        </div>

        {/* Header with User Info */}
        {session && (
          <div className={styles.userSection}>
            <Link href="/account/settings" className={styles.userInfo} onClick={onClose}>
              <Avatar size={48} style={{ backgroundColor: '#05c168' }}>
                {getUserInitials(session?.user?.name)}
              </Avatar>
              <div className={styles.userDetails}>
                <div className={styles.userName}>{session?.user?.name || 'User'}</div>
                <div className={styles.userEmail}>{session?.user?.email || ''}</div>
              </div>
              <Icon name="RightOutlined" size={16} className={styles.chevron} />
            </Link>
          </div>
        )}

        {/* Menu Items */}
        <nav className={styles.menuList}>
          {menuItems.map((item) => {
            if (item.type === 'divider') {
              return <div key={item.key} className={styles.divider} />;
            }

            // Button item (e.g., Sign Out)
            if (item.onClick) {
              return (
                <button
                  key={item.key}
                  className={styles.menuItem}
                  onClick={() => {
                    item.onClick();
                    onClose();
                  }}
                >
                  <Icon name={item.icon} size={20} />
                  <span className={styles.menuLabel}>{item.label}</span>
                </button>
              );
            }

            // Link item
            return (
              <Link key={item.key} href={item.path} className={styles.menuItem} onClick={onClose}>
                <Icon name={item.icon} size={20} />
                <span className={styles.menuLabel}>{item.label}</span>
                <Icon name="RightOutlined" size={16} className={styles.chevron} />
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
};

MoreMenuSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  session: PropTypes.shape({
    user: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
  }),
};

export default MoreMenuSheet;
